import { BellRing, CircleDollarSign, FileText, Package, Truck } from 'lucide-angular';
import type { MaquiUso } from '../../../core/services/maqui.service';

/** Cómo se presenta cada acción que Maqui propone: el botón, lo que dice mientras corre y dónde revisarla. */
export interface MaquiAccionInfo {
  /** Texto del botón de confirmar. */
  boton: string;
  /** Lo que se muestra mientras se ejecuta. */
  enCurso: string;
  /** Pantalla donde el usuario puede ver si la acción quedó hecha. */
  revisarRuta: string;
  /** Texto del enlace a esa pantalla. */
  revisarLabel: string;
  /** Qué tiene que revisar, dicho para el usuario. */
  revisarTexto: string;
}

const ACCIONES: Record<string, MaquiAccionInfo> = {
  CREAR_GUIA_DESPACHO: {
    boton: 'Emitir guía',
    enCurso: 'Emitiendo la guía…',
    revisarRuta: '/facturacion/guias',
    revisarLabel: 'Ver guías',
    revisarTexto: 'Revisa en Guías si la guía quedó emitida antes de pedirla de nuevo.',
  },
  CREAR_GUIA_RETIRO: {
    boton: 'Emitir guía de retiro',
    enCurso: 'Emitiendo la guía de retiro…',
    revisarRuta: '/facturacion/guias',
    revisarLabel: 'Ver guías',
    revisarTexto: 'Revisa en Guías si la guía de retiro quedó emitida antes de pedirla de nuevo.',
  },
  ENVIAR_RECORDATORIO_PAGO: {
    boton: 'Enviar recordatorio',
    enCurso: 'Enviando el recordatorio…',
    revisarRuta: '/facturacion/reportes/por-cobrar',
    revisarLabel: 'Ver cuentas por cobrar',
    revisarTexto: 'Puede que el correo haya salido. Espera unos minutos antes de enviarlo de nuevo.',
  },
  REGISTRAR_PAGO: {
    boton: 'Registrar pago',
    enCurso: 'Registrando el pago…',
    revisarRuta: '/facturacion/reportes/por-cobrar',
    revisarLabel: 'Ver cuentas por cobrar',
    revisarTexto: 'Revisa en Cuentas por cobrar si el pago quedó registrado antes de pedirlo de nuevo.',
  },
};

const ACCION_DESCONOCIDA: MaquiAccionInfo = {
  boton: 'Confirmar',
  enCurso: 'Procesando…',
  revisarRuta: '/dashboard',
  revisarLabel: 'Ir al inicio',
  revisarTexto: 'Revisa si la acción quedó hecha antes de pedirla de nuevo.',
};

/**
 * Cuentas por cobrar solo existe para quien emite DTE por acá (`emiteDteGuard`). Sin eso, el pago y el recordatorio se
 * revisan en la ficha del cliente.
 */
const REVISAR_EN_CLIENTES: Pick<MaquiAccionInfo, 'revisarRuta' | 'revisarLabel'> = {
  revisarRuta: '/clientes',
  revisarLabel: 'Ver clientes',
};

/** La presentación de una acción por su tipo. Un tipo nuevo sin entrada cae en un texto neutro. */
export function accionInfo(tipo: string | null | undefined, modulos?: Partial<MaquiModulosFront>): MaquiAccionInfo {
  const info = (tipo && ACCIONES[tipo]) || ACCION_DESCONOCIDA;
  if (modulos?.emiteDte === false && info.revisarRuta === '/facturacion/reportes/por-cobrar') {
    const revisarTexto = tipo === 'REGISTRAR_PAGO'
      ? 'Revisa en la ficha del cliente si el pago quedó registrado antes de pedirlo de nuevo.'
      : info.revisarTexto;
    return { ...info, ...REVISAR_EN_CLIENTES, revisarTexto };
  }
  return info;
}

/**
 * Si un error al confirmar deja la acción en duda: la red se cortó o el servidor falló, así que pudo haberse ejecutado
 * sin que la respuesta llegara. Un 4xx, en cambio, es un rechazo y no escribió nada.
 */
export function resultadoIncierto(status: number | null | undefined): boolean {
  return status == null || status === 0 || status === 408 || status >= 500;
}

/** Sugerencia del estado de bienvenida. */
export interface MaquiSugerencia {
  icon: typeof Package;
  texto: string;
}

/** Módulos de la cuenta que cambian lo que Maqui ofrece. */
export interface MaquiModulosFront {
  arriendo: boolean;
  /** Si la cuenta emite DTE por acá: sin eso no tiene la pantalla de cuentas por cobrar. */
  emiteDte?: boolean;
}

/**
 * Sugerencias de bienvenida según los módulos. A una cuenta sin arriendo no se le sugiere preguntar por equipos en obra:
 * Maqui no tiene esas herramientas ahí y la respuesta sería un «no puedo».
 */
export function sugerenciasBienvenida(modulos: MaquiModulosFront): MaquiSugerencia[] {
  if (modulos.arriendo) {
    return [
      { icon: Package, texto: '¿Cuántos equipos tengo disponibles?' },
      { icon: Truck, texto: '¿Qué equipos hay en una obra?' },
      { icon: CircleDollarSign, texto: '¿Cuánta deuda tiene un cliente?' },
      { icon: BellRing, texto: 'Envíale un recordatorio de pago a un cliente' },
    ];
  }
  return [
    { icon: CircleDollarSign, texto: '¿Qué clientes tienen deuda?' },
    { icon: FileText, texto: '¿Cuánta deuda tiene un cliente?' },
    { icon: BellRing, texto: 'Envíale un recordatorio de pago a un cliente' },
  ];
}

/** Texto de bienvenida y del coach según los módulos. */
export function textoBienvenida(modulos: MaquiModulosFront): string {
  return modulos.arriendo
    ? 'Conozco tus clientes, obras, equipos y documentos. Pregúntame lo que quieras.'
    : 'Conozco tus clientes, sus deudas, facturas y pagos. Todavía no consulto el taller.';
}

export function textoCoach(modulos: MaquiModulosFront): string {
  return modulos.arriendo
    ? 'Pregúntale por deudas, equipos u obras, o pídele que emita una guía o envíe un recordatorio de pago.'
    : 'Pregúntale por las deudas de tus clientes, o pídele que registre un pago o envíe un recordatorio de pago.';
}

/**
 * El aviso de uso del mes, o null si no hay que mostrar nada. Considera los dos topes: el cupo de consultas y el gasto
 * en USD. Antes solo miraba el cupo, así que Maqui se cortaba por gasto sin aviso.
 */
export function avisoDeUso(uso: MaquiUso | null | undefined): { texto: string; alerta: boolean } | null {
  if (!uso) return null;
  if (uso.presupuestoAlcanzado) {
    return { texto: 'Maqui alcanzó su límite de uso de este mes. Se renueva el día 1.', alerta: true };
  }
  if (uso.alcanzado) {
    return { texto: 'Alcanzaste las consultas a Maqui de este mes. Se renuevan el día 1.', alerta: true };
  }
  const porGasto = uso.topeCostoUsd && uso.topeCostoUsd > 0 && uso.costoUsdMes != null
    ? Math.round((uso.costoUsdMes / uso.topeCostoUsd) * 100)
    : null;
  const pct = Math.max(uso.porcentaje ?? 0, porGasto ?? 0);
  if (pct >= 70) {
    return { texto: `Uso de Maqui este mes: ${Math.min(pct, 99)}%`, alerta: pct >= 90 };
  }
  return null;
}
